import React from "react";
import {Navigate} from "react-router-dom";

import {APICore} from "../helpers/api/apiCore";
import {useSyncedPermissions} from "../helpers/can";
import Forbidden from "../pages/Forbidden";

/**
 * Private Route forces the authorization before the route can be accessed
 * @param {*} param0
 * @returns
 */
const PrivateRoute = ({children, permissions, ...rest}: any) => {
    // Subscribe the whole private subtree to the permission set, and rebuild the casl
    // ability during THIS render so every CAN() below sees the current rules. Without it
    // a permission that arrives mid-session (see helpers/usePermissionRefresh.ts) never
    // reaches the UI: CAN() reads a module-level object React does not track, so gated
    // buttons stayed missing until something unrelated forced a re-render.
    // Hooks run before any conditional return below.
    const livePermissions = useSyncedPermissions();
    // Recomputed only when the permission array identity changes, which is once per
    // refresh at most — not on every render.
    const signature = React.useMemo(() => livePermissions.join("|"), [livePermissions]);

    const api = new APICore();
    if (!api.isUserAuthenticated()) {
        // not logged in so redirect to login page with the return url
        return (
            <Navigate
                // state={from: props['path']}
                to={{
                    pathname: "/auth/login",
                    // state: { from: props['path'] },
                }}
            />
        );
    }

    // Prefer the live (refreshed) list; fall back to the login-time snapshot in
    // sessionStorage before the refresh has landed.
    const granted: string[] = livePermissions.length
        ? livePermissions
        : api.getLoggedInUser()?.permission ?? [];

    // Authenticated but the route needs a permission the user's role doesn't grant:
    // show a friendly 403 (was a silent redirect to "/", which looked like a bug to a
    // user who had just been narrowed to a department role).
    if (permissions && permissions.length > 0 && !permissions?.some((item: string) => granted.includes(item))) {
        return <Forbidden/>;
    }

    // Re-rendering this component is NOT enough to refresh the page below it. The route
    // element is built once in routes/index.tsx and handed down unchanged through
    // PortalRouteLayout, so React sees an identical element on every re-render and bails
    // out of the whole subtree — the page would keep whatever CAN() returned the first
    // time. Keying on the permission set remounts the subtree when, and only when, the
    // set actually changes. Unchanged permissions (the normal case, every render) keep
    // the same key and cost nothing.
    return <React.Fragment key={signature}>{children}</React.Fragment>;
};

export default PrivateRoute;
